import { Icona } from '../../components/Icona';
import { useEffect, useMemo, useState } from 'react';
import { ordini, ETICHETTE_ORIGINE_ACQUISTO } from '../../api/acquisti';
import type {
  OffertaPerAcquisto,
  OrdineCreato,
  PreparazioneOrdini,
  RigaDaPreparare,
} from '../../api/acquisti';
import { formattaData, formattaImporto, formattaNumero, formattaPercentuale, oggiIso } from '../../lib/formato';
import { numeroDecimaleOZero } from '../../lib/numeri';
import { decisioneIniziale, preparaRighePerOrdine, raggruppaPerFornitore } from './decisioni';
import type { Decisione } from './decisioni';
import { ScegliFornitore } from './ScegliFornitore';

interface Props {
  righeSelezionate: string[];
  onChiudi: () => void;
  onOrdiniCreati: () => void;
}

export function PannelloOrdini({ righeSelezionate, onChiudi, onOrdiniCreati }: Props) {
  const [preparazione, setPreparazione] = useState<PreparazioneOrdini | null>(null);
  const [decisioni, setDecisioni] = useState<Record<string, Decisione>>({});
  const [consegna, setConsegna] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [creati, setCreati] = useState<OrdineCreato[] | null>(null);

  useEffect(() => {
    let annullato = false;
    setPreparazione(null);
    setErrore(null);

    ordini
      .prepara(righeSelezionate)
      .then((p) => {
        if (annullato) return;
        setPreparazione(p);
        setDecisioni(Object.fromEntries(p.righe.map((riga) => [riga.rigaRichiestaId, decisioneIniziale(riga)])));
      })
      .catch((e: Error) => !annullato && setErrore(e.message));

    return () => {
      annullato = true;
    };
  }, [righeSelezionate]);

  const righe = preparazione?.righe ?? [];

  // Il raggruppamento si mostra prima di creare, non dopo: «uno per fornitore» è la
  // regola che sorprende, e vederla applicata prima di premere il tasto evita la
  // domanda «perché mi ha fatto due ordini».
  const gruppi = useMemo(() => raggruppaPerFornitore(righe, decisioni), [righe, decisioni]);

  function cambia(id: string, modifica: Partial<Decisione>) {
    setDecisioni((precedenti) => ({ ...precedenti, [id]: { ...precedenti[id]!, ...modifica } }));
  }

  function scegliOfferta(riga: RigaDaPreparare, offerta: OffertaPerAcquisto) {
    cambia(riga.rigaRichiestaId, {
      fornitore: offerta.codiceFornitoreMago,
      altroNome: null,
      costoNetto: String(offerta.costoNetto),
      codiceArticoloFornitore: offerta.codiceArticoloFornitore,
    });
  }

  async function crea() {
    const esito = preparaRighePerOrdine(righe, decisioni);
    if (!esito.ok) {
      setErrore(esito.errore);
      return;
    }

    setInCorso(true);
    setErrore(null);
    try {
      const risultato = await ordini.crea(
        esito.righe,
        consegna || undefined,
        indirizzo.trim() || undefined,
      );
      setCreati(risultato);
      onOrdiniCreati();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  if (creati) {
    return (
      <section className="pannello-ordini esito">
        <h2>{creati.length === 1 ? 'Ordine creato' : `${creati.length} ordini creati`}</h2>
        <ul className="ordini-creati">
          {creati.map((ordine) => (
            <li key={ordine.id}>
              <strong>{ordine.numero}</strong>
              <span>{ordine.fornitore ?? ordine.codiceFornitoreMago}</span>
              <span>{ordine.righe === 1 ? '1 riga' : `${ordine.righe} righe`}</span>
              <span className="destra">{formattaImporto(ordine.totale)}</span>
            </li>
          ))}
        </ul>
        {/* Nascono in bozza di proposito: fra il decidere e il mandare c'è una rilettura. */}
        <p className="nota">
          Sono in bozza: si inviano dalla scheda dell'ordine, che è dove si mette la data di
          consegna definitiva e si congela l'anagrafica del fornitore.
        </p>
        <button type="button" className="primario" onClick={onChiudi}>
          Torna alla lista
        </button>
      </section>
    );
  }

  if (errore && !preparazione) {
    return (
      <section className="pannello-ordini">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </section>
    );
  }

  if (!preparazione) {
    return (
      <section className="pannello-ordini">
        <p className="attesa">Leggo le offerte…</p>
      </section>
    );
  }

  return (
    <section className="pannello-ordini">
      <header>
        <div>
          <h2>Da chi comprare</h2>
          <p>
            {righe.length === 1 ? 'Una riga' : `${righe.length} righe`} selezionate. Per ognuna
            scegli il fornitore: le offerte sono in ordine di prezzo, la prima è la più
            conveniente di oggi.
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi il pannello">
          <Icona nome="chiudi" />
        </button>
      </header>

      {preparazione.mancanti.length > 0 && (
        <p className="avviso">
          {preparazione.mancanti.length === 1
            ? 'Una riga selezionata non esiste più ed è stata ignorata.'
            : `${preparazione.mancanti.length} righe selezionate non esistono più e sono state ignorate.`}
        </p>
      )}

      <ol className="righe-da-ordinare">
        {righe.map((riga) => {
          const decisione = decisioni[riga.rigaRichiestaId];
          if (!decisione) return null;

          const altro = decisione.fornitore !== null && !riga.offerte.some(
            (o) => o.codiceFornitoreMago === decisione.fornitore,
          );

          return (
            <li
              key={riga.rigaRichiestaId}
              className={[
                !riga.ordinabile ? 'bloccata' : '',
                decisione.esclusa ? 'esclusa' : '',
              ].join(' ').trim()}
            >
              <div className="riga-testata">
                <div className="riga-cosa">
                  <p className="descrizione">
                    {riga.descrizione}
                    {riga.daOrdinare !== riga.quantita && (
                      <em className="parziale">
                        {formattaNumero(riga.daOrdinare)} di {formattaNumero(riga.quantita)}{' '}
                        {riga.unitaMisura}
                      </em>
                    )}
                  </p>
                  <p className="riga-provenienza">
                    {riga.siglaProduttore && (
                      <span className="chiave">
                        {riga.siglaProduttore} {riga.codiceArticoloProduttore}
                      </span>
                    )}
                    {!riga.articoloId && <span className="da-codificare">da codificare</span>}
                    <span>
                      {riga.numeroRichiesta}
                      {riga.commessa && ` · commessa ${riga.commessa}`}
                    </span>
                    <span className={riga.giorniAlFabbisogno < 0 ? 'in-ritardo' : ''}>
                      serve il {formattaData(riga.dataFabbisogno)}
                      {riga.giorniAlFabbisogno < 0 &&
                        ` · in ritardo di ${-riga.giorniAlFabbisogno} giorni`}
                    </span>
                  </p>
                </div>

                {riga.ordinabile && (
                  <label className="escludi">
                    <input
                      type="checkbox"
                      checked={decisione.esclusa}
                      onChange={(e) => cambia(riga.rigaRichiestaId, { esclusa: e.target.checked })}
                    />
                    non ora
                  </label>
                )}
              </div>

              {riga.problema && <p className="errore riga-problema">{riga.problema}</p>}

              {riga.ordinabile && !decisione.esclusa && (
                <>
                  <ScostamentoStima riga={riga} />

                  {riga.offerte.length > 0 ? (
                    <table className="offerte">
                      <tbody>
                        {riga.offerte.map((offerta) => (
                          <tr
                            key={offerta.codiceFornitoreMago}
                            className={
                              offerta.codiceFornitoreMago === decisione.fornitore ? 'scelta' : ''
                            }
                          >
                            <td className="radio">
                              <input
                                type="radio"
                                name={`fornitore-${riga.rigaRichiestaId}`}
                                checked={offerta.codiceFornitoreMago === decisione.fornitore}
                                onChange={() => scegliOfferta(riga, offerta)}
                                aria-label={`Compra da ${offerta.fornitore ?? offerta.codiceFornitoreMago}`}
                              />
                            </td>
                            <td>
                              <span className="nome">
                                {offerta.fornitore ?? offerta.codiceFornitoreMago}
                                {offerta.migliore && <em className="bollino">migliore</em>}
                                {offerta.codiceFornitoreMago === riga.fornitoreSuggeritoMago && (
                                  <em className="bollino tenue">suggerito in richiesta</em>
                                )}
                              </span>
                              <span className="dettaglio">
                                {offerta.codiceArticoloFornitore && (
                                  <>{offerta.codiceArticoloFornitore} · </>
                                )}
                                {ETICHETTE_ORIGINE_ACQUISTO[offerta.origine] ?? offerta.origine}
                                {offerta.scontoTesto && <em className="sconto">{offerta.scontoTesto}</em>}
                                {offerta.confezioneMinima !== null && (
                                  <> · conf. da {formattaNumero(offerta.confezioneMinima)}</>
                                )}
                              </span>
                            </td>
                            <td className="destra costo">{formattaImporto(offerta.costoNetto)}</td>
                            <td className="destra scarto">
                              {offerta.differenzaDallaMigliore === null ||
                              offerta.differenzaDallaMigliore === 0 ? (
                                '—'
                              ) : (
                                <>
                                  +{formattaImporto(offerta.differenzaDallaMigliore)}
                                  {offerta.differenzaPercentuale !== null && (
                                    <small>+{formattaPercentuale(offerta.differenzaPercentuale)}</small>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="destra">
                              {offerta.scontoSulListinoProduttore === null
                                ? '—'
                                : formattaPercentuale(offerta.scontoSulListinoProduttore)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="senza-offerte">
                      {riga.articoloId
                        ? 'Nessuno ha quotato questo articolo: scegli il fornitore e scrivi il prezzo.'
                        : 'Riga scritta a mano, senza articolo: scegli il fornitore e scrivi il prezzo.'}
                    </p>
                  )}

                  <details className="altro-fornitore" open={altro || riga.offerte.length === 0}>
                    <summary>
                      {altro
                        ? `Fornitore fuori offerte: ${decisione.altroNome ?? decisione.fornitore}`
                        : 'Un altro fornitore'}
                    </summary>
                    <ScegliFornitore
                      valore={decisione.fornitore}
                      onScelto={(codice, denominazione) =>
                        cambia(riga.rigaRichiestaId, {
                          fornitore: codice,
                          altroNome: denominazione,
                          codiceArticoloFornitore: null,
                        })
                      }
                    />
                  </details>

                  <div className="quanto">
                    <label>
                      Quantità
                      <input
                        type="text"
                        inputMode="decimal"
                        value={decisione.quantita}
                        onChange={(e) => cambia(riga.rigaRichiestaId, { quantita: e.target.value })}
                      />
                      {riga.unitaMisura && <span className="um">{riga.unitaMisura}</span>}
                    </label>
                    <label>
                      Costo netto
                      <input
                        type="text"
                        inputMode="decimal"
                        value={decisione.costoNetto}
                        onChange={(e) => cambia(riga.rigaRichiestaId, { costoNetto: e.target.value })}
                      />
                    </label>
                    <label>
                      Codice del fornitore
                      <input
                        type="text"
                        placeholder="quello che legge lui sull'ordine"
                        value={decisione.codiceArticoloFornitore ?? ''}
                        onChange={(e) =>
                          cambia(riga.rigaRichiestaId, {
                            codiceArticoloFornitore: e.target.value || null,
                          })
                        }
                      />
                    </label>
                    <p className="importo-riga">
                      {formattaImporto(
                        numeroDecimaleOZero(decisione.quantita) *
                          numeroDecimaleOZero(decisione.costoNetto),
                      )}
                    </p>
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ol>

      <footer>
        <div className="consegna">
          <label>
            Consegna richiesta
            <input
              type="date"
              min={oggiIso()}
              value={consegna}
              onChange={(e) => setConsegna(e.target.value)}
            />
          </label>
          <label className="larga">
            Indirizzo di consegna
            <input
              type="text"
              placeholder="in bianco: la sede"
              value={indirizzo}
              onChange={(e) => setIndirizzo(e.target.value)}
            />
          </label>
        </div>

        <div className="riepilogo">
          {gruppi.length === 0 ? (
            <p className="vuoto">Nessuna riga pronta: scegli almeno un fornitore.</p>
          ) : (
            <>
              <p className="conteggio">
                {gruppi.length === 1
                  ? 'Verrà creato un ordine'
                  : `Verranno creati ${gruppi.length} ordini, uno per fornitore`}
              </p>
              <ul>
                {gruppi.map((gruppo) => (
                  <li key={gruppo.codice}>
                    <strong>{gruppo.nome}</strong>
                    <span>{gruppo.righe === 1 ? '1 riga' : `${gruppo.righe} righe`}</span>
                    <span className="destra">{formattaImporto(gruppo.totale)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {errore && <p className="errore">{errore}</p>}

        <div className="azioni">
          <button type="button" onClick={onChiudi}>
            Annulla
          </button>
          <button
            type="button"
            className="primario"
            disabled={inCorso || gruppi.length === 0}
            onClick={crea}
          >
            {inCorso ? 'Creo…' : gruppi.length > 1 ? `Crea ${gruppi.length} ordini` : 'Crea l’ordine'}
          </button>
        </div>
      </footer>
    </section>
  );
}

/**
 * Il costo di quando la riga è stata chiesta contro quello di oggi. Si mostra solo se
 * sono diversi: è l'unico caso in cui dice qualcosa, e allora dice una cosa che conta —
 * il listino è cambiato fra la richiesta e l'acquisto.
 */
function ScostamentoStima({ riga }: { riga: RigaDaPreparare }) {
  if (riga.costoStimato === null || riga.migliorCostoOggi === null) return null;
  if (Math.abs(riga.costoStimato - riga.migliorCostoOggi) < 0.0001) return null;

  const salito = riga.migliorCostoOggi > riga.costoStimato;
  const scarto = Math.abs(riga.migliorCostoOggi - riga.costoStimato);

  return (
    <p className={`scostamento ${salito ? 'peggio' : 'meglio'}`}>
      Quando è stata chiesta il migliore era {formattaImporto(riga.costoStimato)}; oggi è{' '}
      {formattaImporto(riga.migliorCostoOggi)} — {salito ? 'in su' : 'in giù'} di{' '}
      {formattaImporto(scarto)}.
    </p>
  );
}

